import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const ProductDemoSection = () => {
  const [ref, isVisible] = useScrollAnimation(0.2);
  const [activeTab, setActiveTab] = useState(0);

  const demos = [
    {
      title: "Visual Workflow Editor",
      description:
        "Drag and drop nodes to create powerful AI workflows without writing a single line of code.",
      image: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      ),
    },
    {
      title: "Real-time Execution",
      description:
        "Watch your agents work in real-time with live logs and instant feedback on every action.",
      image: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    {
      title: "Smart Integrations",
      description:
        "Connect seamlessly with your favorite tools and automate your entire workflow ecosystem.",
      image: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
    },
  ];

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block px-4 py-2 mb-4 text-sm font-semibold text-accent-700 bg-accent-100 rounded-full"
          >
            See it in action
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Design Workflows That
            <span className="text-gradient"> Deliver</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience the power of visual AI automation with our intuitive
            interface.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Tabs */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-4"
          >
            {demos.map((demo, index) => (
              <motion.div
                key={index}
                whileHover={{ x: 8 }}
                onClick={() => setActiveTab(index)}
                className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
                  activeTab === index
                    ? "bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-xl"
                    : "glass-effect hover:bg-white/90"
                }`}
              >
                <div className="flex items-start gap-4">
                  <motion.div
                    animate={{ scale: activeTab === index ? 1.2 : 1 }}
                    transition={{ duration: 0.3 }}
                    className="text-4xl"
                  >
                    {demo.image}
                  </motion.div>
                  <div>
                    <h3
                      className={`text-xl font-bold mb-2 ${
                        activeTab === index ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {demo.title}
                    </h3>
                    <p
                      className={`${
                        activeTab === index ? "text-white/90" : "text-gray-600"
                      }`}
                    >
                      {demo.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Right side - Demo visualization */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative h-[600px]"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, scale: 0.9, rotateY: -20 }}
                animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                exit={{ opacity: 0, scale: 0.9, rotateY: 20 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0"
              >
                {/* Mock UI Container */}
                <div className="h-full glass-effect rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
                  {/* Mock Browser Chrome */}
                  <div className="bg-gray-100 px-4 py-3 flex items-center gap-2 border-b border-gray-200">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                    <div className="flex-1 mx-4 bg-white rounded-md px-3 py-1 text-xs text-gray-600">
                      app.youragent.ai/workflows
                    </div>
                  </div>

                  {/* Demo Content */}
                  <div className="p-8 bg-gradient-to-br from-gray-50 to-white h-full">
                    {activeTab === 0 && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-4"
                      >
                        {/* Node connections */}
                        <div className="flex items-center gap-4">
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2 }}
                            className="w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl shadow-lg flex items-center justify-center"
                          >
                            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                            </svg>
                          </motion.div>
                          <motion.div
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ delay: 0.4 }}
                            className="h-1 w-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded"
                          />
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.6 }}
                            className="w-20 h-20 bg-gradient-to-br from-accent-400 to-accent-600 rounded-xl shadow-lg flex items-center justify-center"
                          >
                            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </motion.div>
                          <motion.div
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ delay: 0.8 }}
                            className="h-1 w-16 bg-gradient-to-r from-accent-500 to-primary-500 rounded"
                          />
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 1 }}
                            className="w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl shadow-lg flex items-center justify-center"
                          >
                            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </motion.div>
                        </div>

                        {/* Info cards */}
                        <motion.div
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 1.2 }}
                          className="mt-8 space-y-3"
                        >
                          {[
                            "Trigger: New email received",
                            "Action: Process with AI",
                            "Output: Send response",
                          ].map((text, i) => (
                            <motion.div
                              key={i}
                              initial={{ x: -20, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ delay: 1.4 + i * 0.1 }}
                              className="p-4 bg-white rounded-lg shadow-md"
                            >
                              <p className="text-sm text-gray-700">{text}</p>
                            </motion.div>
                          ))}
                        </motion.div>
                      </motion.div>
                    )}

                    {activeTab === 1 && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-4"
                      >
                        {/* Execution log */}
                        {[
                          {
                            status: "success",
                            text: "Workflow initialized",
                            time: "0.2s",
                          },
                          {
                            status: "success",
                            text: "Data fetched successfully",
                            time: "0.5s",
                          },
                          {
                            status: "processing",
                            text: "Processing with AI...",
                            time: "1.2s",
                          },
                          {
                            status: "pending",
                            text: "Sending response",
                            time: "-",
                          },
                        ].map((log, i) => (
                          <motion.div
                            key={i}
                            initial={{ x: -30, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: i * 0.2 }}
                            className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-md"
                          >
                            <div
                              className={`w-3 h-3 rounded-full ${
                                log.status === "success"
                                  ? "bg-green-500"
                                  : log.status === "processing"
                                  ? "bg-blue-500 animate-pulse"
                                  : "bg-gray-300"
                              }`}
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {log.text}
                              </p>
                            </div>
                            <span className="text-xs text-gray-500">
                              {log.time}
                            </span>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}

                    {activeTab === 2 && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="grid grid-cols-2 gap-4"
                      >
                        {[
                          {
                            name: "Gmail",
                            icon: (
                              <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                              </svg>
                            ),
                            color: "from-red-400 to-red-600",
                          },
                          {
                            name: "Slack",
                            icon: (
                              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                            ),
                            color: "from-purple-400 to-purple-600",
                          },
                          {
                            name: "HubSpot",
                            icon: (
                              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            ),
                            color: "from-orange-400 to-orange-600",
                          },
                          {
                            name: "Calendar",
                            icon: (
                              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            ),
                            color: "from-blue-400 to-blue-600",
                          },
                        ].map((integration, i) => (
                          <motion.div
                            key={i}
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ delay: i * 0.1, type: "spring" }}
                            whileHover={{ scale: 1.05, y: -4 }}
                            className={`p-6 bg-gradient-to-br ${integration.color} rounded-2xl shadow-xl cursor-pointer`}
                          >
                            <div className="mb-2">
                              {integration.icon}
                            </div>
                            <p className="text-white font-semibold">
                              {integration.name}
                            </p>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Floating elements */}
            <motion.div
              animate={{
                y: [0, -20, 0],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br from-primary-400 to-accent-400 rounded-full blur-2xl opacity-30"
            />
            <motion.div
              animate={{
                y: [0, 20, 0],
                rotate: [0, -5, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -bottom-8 -left-8 w-40 h-40 bg-gradient-to-br from-accent-400 to-primary-400 rounded-full blur-2xl opacity-30"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProductDemoSection;
